import { SUITS, rankValueAceHigh, suitOf } from '../core/cards.js';
/**
 * Pusoy rules: the poker evaluator, the misset boundary, the naturals catalogue,
 * pairwise scoring and the auto-arranger.
 *
 * Ported from the frozen Baraha Pa More module (D3). Everything here is
 * integer-only, suit-blind and order-free, because these same numbers have to
 * come out of a future client-side port bit for bit — the golden vectors are
 * shared between the two and a rounding difference would pay the wrong seat.
 */
const RV = rankValueAceHigh; // A=14 … 2=2
/**
 * Deal and display order: strongest rank first, suits in deck order to break the
 * tie. The order has to be TOTAL, not merely sorted by rank — the auto-arranger
 * enumerates splits by index and settles equal-value splits by which came first,
 * so two cards of the same rank sitting in either order would give a replay a
 * different board from the one the table saw.
 */
export function byStrength(a, b) {
    const d = RV(b) - RV(a);
    return d !== 0 ? d : SUITS.indexOf(suitOf(a)) - SUITS.indexOf(suitOf(b));
}
// ── §3 the evaluator ────────────────────────────────────────────────────────
/**
 * Hand strength as ONE integer, so comparison is `<`.
 *
 * Packed as ((((cat*16 + k0)*16 + k1)*16 + k2)*16 + k3)*16 + k4 with cat 0..8 and
 * each kicker 0..14 (0 = unused). The maximum is ~9.5M, well inside 2^31, so it
 * is exact in JS and in every language this is ported to.
 */
export function pack(cat, k0, k1, k2, k3, k4) {
    return ((((cat * 16 + k0) * 16 + k1) * 16 + k2) * 16 + k3) * 16 + k4;
}
/** Hand class 0..8: high card … straight flush. */
export function catOf(key) {
    return Math.floor(key / 1048576); // 16^5
}
/** The five kicker fields [k0..k4], most significant first. */
export function fieldsOf(key) {
    const v = key % 1048576;
    const f = [0, 0, 0, 0, 0];
    for (let i = 0; i < 5; i++)
        f[i] = Math.floor(v / Math.pow(16, 4 - i)) % 16;
    return f;
}
/** Hand class names, indexed by category. The results screen renders these. */
export const CATEGORIES = [
    'highCard',
    'pair',
    'twoPair',
    'trips',
    'straight',
    'flush',
    'fullHouse',
    'quads',
    'straightFlush',
];
export function categoryOf(key) {
    return CATEGORIES[catOf(key)] ?? 'highCard';
}
/**
 * Rank-count groups for one row, sorted by count down then rank down — the
 * order every packed key is read in.
 *
 * Written into module-level scratch arrays rather than returned, and filled by
 * counting instead of sorting. This runs 144,000 times per auto-arrangement, and
 * a Map plus a comparator sort per call is the difference between a table that
 * feels instant and one that does not. Safe because it is synchronous and never
 * re-entered: every caller reads GR/GC out before doing anything else.
 */
const CNT = new Array(15).fill(0);
const GR = new Array(6).fill(0); // group rank
const GC = new Array(6).fill(0); // group count
function groupRow(rv) {
    for (let r = 2; r <= 14; r++)
        CNT[r] = 0;
    for (const r of rv)
        CNT[r] = (CNT[r] ?? 0) + 1;
    let n = 0;
    // Ranks descending first, so equal counts stay in rank order below.
    for (let r = 14; r >= 2; r--) {
        const c = CNT[r] ?? 0;
        if (c === 0)
            continue;
        GR[n] = r;
        GC[n] = c;
        n++;
    }
    // Insertion sort by count descending, STRICTLY greater, so equal counts keep
    // the rank-descending order they were collected in.
    for (let i = 1; i < n; i++) {
        const r = GR[i] ?? 0;
        const c = GC[i] ?? 0;
        let j = i - 1;
        while (j >= 0 && (GC[j] ?? 0) < c) {
            GR[j + 1] = GR[j] ?? 0;
            GC[j + 1] = GC[j] ?? 0;
            j--;
        }
        GR[j + 1] = r;
        GC[j + 1] = c;
    }
    return n;
}
/**
 * The five-card core, on precomputed rank values and suits.
 *
 * `straight` and `flush` come back alongside the key because the naturals and
 * the auto-arranger need them per row, and neither can be read back out of the
 * key once a pair has outranked the straight.
 */
export function evalFive(rv, suits) {
    const s0 = suits[0];
    const isFlush = s0 !== undefined &&
        suits.length === 5 &&
        suits[1] === s0 &&
        suits[2] === s0 &&
        suits[3] === s0 &&
        suits[4] === s0;
    const n = groupRow(rv);
    if (n === 0)
        throw new Error('pusoy: evalFive on an empty row');
    const r0 = GR[0] ?? 0;
    const r1 = GR[1] ?? 0;
    const r2 = GR[2] ?? 0;
    const r3 = GR[3] ?? 0;
    const r4 = GR[4] ?? 0;
    const c0 = GC[0] ?? 0;
    const c1 = GC[1] ?? 0;
    // Five distinct ranks, so GR is plain rank order here — a pair is never a
    // straight, and the ace does not wrap (Q-K-A-2-3 is nothing at all).
    const wheel = n === 5 && r0 === 14 && r1 === 5 && r2 === 4 && r3 === 3 && r4 === 2;
    const straight = n === 5 && (r0 - r4 === 4 || wheel);
    const top = straight ? (wheel ? 5 : r0) : 0;
    let key;
    if (straight && isFlush)
        key = pack(8, top, 0, 0, 0, 0);
    else if (c0 === 4)
        key = pack(7, r0, r1, 0, 0, 0);
    else if (c0 === 3 && c1 === 2)
        key = pack(6, r0, r1, 0, 0, 0);
    // A flush and a high card both have five distinct ranks, so the groups are
    // already the kickers, highest first.
    else if (isFlush)
        key = pack(5, r0, r1, r2, r3, r4);
    else if (straight)
        key = pack(4, top, 0, 0, 0, 0);
    else if (c0 === 3)
        key = pack(3, r0, r1, r2, 0, 0);
    // Two pair: the groups are sorted, so r0 is the high pair, r1 the low pair and
    // r2 the kicker. Reading them in any other order swaps QQ-JJ hands.
    else if (c0 === 2 && c1 === 2)
        key = pack(2, r0, r1, r2, 0, 0);
    else if (c0 === 2)
        key = pack(1, r0, r1, r2, r3, 0);
    else
        key = pack(0, r0, r1, r2, r3, r4);
    return { key, straight, flush: isFlush };
}
/** Back and middle rows. Precondition: exactly five distinct cards. */
export function rank5(hand) {
    return evalFive(hand.map(RV), hand.map(suitOf)).key;
}
/**
 * The three-card core — FRONT ONLY, and deliberately blind to straights and
 * flushes. Q-J-10 of spades in front is a high card, not a straight flush. This
 * is the rule most home tables agree on and the one the reference implements;
 * scoring it any other way would let a front row outrank a middle it should
 * have lost to, and the misset boundary is built on top of these keys.
 */
export function evalThree(rv) {
    const n = groupRow(rv);
    if (n === 0)
        throw new Error('pusoy: evalThree on an empty row');
    const r0 = GR[0] ?? 0;
    const r1 = GR[1] ?? 0;
    const r2 = GR[2] ?? 0;
    const c0 = GC[0] ?? 0;
    if (c0 === 3)
        return pack(3, r0, 0, 0, 0, 0);
    // Pair first, then the odd card — three distinct ranks are already in order.
    if (c0 === 2)
        return pack(1, r0, r1, 0, 0, 0);
    return pack(0, r0, r1, r2, 0, 0);
}
export function rank3(hand) {
    return evalThree(hand.map(RV));
}
/**
 * Three-card run test — used by the naturals only, never by rank3.
 *
 * Both ends wrap here: A-2-3 and Q-K-A are runs. A three-straight natural is a
 * shape claim about the whole thirteen, not a ranking, so the ace is allowed to
 * be low or high the way it is in a five-card straight.
 */
export function is3StraightRv(rv) {
    const s = Array.from(new Set(rv)).sort((a, b) => a - b);
    if (s.length !== 3)
        return false;
    const a = s[0] ?? 0;
    const b = s[1] ?? 0;
    const c = s[2] ?? 0;
    if (c - a === 2)
        return true;
    if (a === 2 && b === 3 && c === 14)
        return true;
    if (a === 12 && b === 13 && c === 14)
        return true;
    return false;
}
export function keyOf(hand) {
    return hand.length === 3 ? rank3(hand) : rank5(hand);
}
/** -1, 0 or 1. Rows of the same length only; suits never break a tie. */
export function compareHands(a, b) {
    const ka = keyOf(a);
    const kb = keyOf(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
}
/**
 * The middle >= front half of the misset boundary, on keys.
 *
 * A five-card key and a three-card key are not comparable as integers: the
 * middle carries kickers the front does not have, so QQ-9-8-7 packs above
 * QQ-9 on fields the front never filled in. The rule is therefore stated on the
 * fields the FRONT actually defines — three ranks for a high card, pair rank
 * plus one kicker for a pair, the trips rank for trips — and a tie on those
 * counts as middle >= front, because the middle's extra kickers can only help
 * it. Deterministic and cheap, which is what keeps two implementations from
 * disagreeing about who missed.
 */
export function middleGeFrontKeys(km, kf) {
    const cm = catOf(km);
    const cf = catOf(kf);
    if (cm !== cf)
        return cm > cf;
    const n = cm === 0 ? 3 : cm === 1 ? 2 : 1; // fields the front defines for cats {0,1,3}
    const fm = fieldsOf(km);
    const ff = fieldsOf(kf);
    for (let i = 0; i < n; i++) {
        const a = fm[i] ?? 0;
        const b = ff[i] ?? 0;
        if (a !== b)
            return a > b;
    }
    return true;
}
export function middleGeFront(mid5, front3) {
    return middleGeFrontKeys(rank5(mid5), rank3(front3));
}
/**
 * The row-strength law: back >= middle >= front. Breaking it is a MISSET (the
 * reference calls it a foul) — the board is not rejected, it is penalised, see
 * pairNet().
 */
export function notFoul(back, middle, front) {
    return rank5(back) >= rank5(middle) && middleGeFront(middle, front);
}
function suitCounts(hand) {
    const m = { S: 0, H: 0, D: 0, C: 0 };
    for (const c of hand)
        m[suitOf(c)] = (m[suitOf(c)] ?? 0) + 1;
    return m;
}
function rankCounts(hand) {
    const m = new Map();
    for (const c of hand)
        m.set(RV(c), (m.get(RV(c)) ?? 0) + 1);
    return m;
}
/** Every C(n,k) index combination of [0..n). */
function combinations(n, k) {
    const out = [];
    const idx = [];
    const go = (start, need) => {
        if (need === 0) {
            out.push([...idx]);
            return;
        }
        for (let i = start; i <= n - need; i++) {
            idx.push(i);
            go(i + 1, need - 1);
            idx.pop();
        }
    };
    go(0, k);
    return out;
}
const combCache = new Map();
function combos(n, k) {
    const key = `${n}:${k}`;
    let c = combCache.get(key);
    if (!c) {
        c = combinations(n, k);
        combCache.set(key, c);
    }
    return c;
}
/**
 * The 56 ways to split the eight cards left after the back into a middle and a
 * front. Fixed, built once, and shared by the naturals scan and the
 * auto-arranger — both walk the splits in exactly this order, which is what
 * makes "the first split that matches" a reproducible answer rather than an
 * implementation detail.
 */
const MID_FRONT_8 = combos(8, 5).map((m) => {
    const inMid = new Set(m);
    const f = [];
    for (let i = 0; i < 8; i++)
        if (!inMid.has(i))
            f.push(i);
    return [m, f];
});
/** One suit throughout. Works for a five-card row and for the three-card front. */
function sameSuit(hand) {
    const s = hand.map(suitOf);
    return s.every((x) => x === s[0]);
}
/** Straight-ness of a five-card row, from the evaluator — one definition of the
 * rule, so the naturals cannot disagree with the ranking about what a straight
 * is. */
function isStraight5(hand) {
    return evalFive(hand.map(RV), hand.map(suitOf)).straight;
}
/**
 * A SOUND cheap gate for the two arrangement-dependent naturals: could a
 * three-flush or a three-straight even exist in this hand?
 *
 * It never returns false when one exists, so gating the 72k-split scan on it
 * leaves detectNatural's answer unchanged.
 *
 * It is NOT a rare-case filter, whatever the reference's comment claims: five
 * consecutive ranks turn up in most thirteens, so it lets ~55% of random hands
 * through (measured: 1644/3000). What actually keeps detectNatural cheap is the
 * per-back skip inside the scan below — a back that is neither a flush nor a
 * straight cannot start any enabled natural, so its 56 middle/front partitions
 * are never built. Together that is ~2ms on an ordinary hand; the gate alone
 * would leave it an order of magnitude worse.
 */
function threeXPossible(rc, sc) {
    // Three flushes ⟺ the suit counts partition into monochrome 5/5/3 groups, i.e.
    // the sorted suit-count multiset is 5-5-3, 8-5, 10-3 or 13.
    const s = Object.values(sc).sort((a, b) => b - a);
    const s0 = s[0] ?? 0;
    const s1 = s[1] ?? 0;
    const s2 = s[2] ?? 0;
    if (s0 === 13 || (s0 === 10 && s1 === 3) || (s0 === 8 && s1 === 5) || (s0 === 5 && s1 === 5 && s2 === 3)) {
        return true;
    }
    // Three straights: no rank may appear more than three times, and some window
    // of five consecutive present ranks must exist (a five-card straight).
    for (const n of rc.values())
        if (n > 3)
            return false;
    const has = (r) => rc.has(r);
    for (let lo = 2; lo <= 10; lo++) {
        if (has(lo) && has(lo + 1) && has(lo + 2) && has(lo + 3) && has(lo + 4))
            return true;
    }
    return has(14) && has(2) && has(3) && has(4) && has(5); // the wheel
}
/**
 * Canonical tie vector for a three-X natural: the stronger five-card row first,
 * then the weaker one, then the front.
 *
 * The split enumerator may hand the stronger flush to either back or middle, so
 * two same-kind naturals have to be compared strongest-row-first — otherwise the
 * winner flips depending on which split happened to be found.
 */
function natTieVec(back, middle, front) {
    const kb = rank5(back);
    const km = rank5(middle);
    return [Math.max(kb, km), Math.min(kb, km), rank3(front)];
}
function canonDragon(hand13, kind, cfg) {
    // Every rank appears once, so sorting by rank descending is a total order:
    // strongest five back, next five middle, last three front.
    const sorted = [...hand13].sort((a, b) => RV(b) - RV(a));
    return {
        kind,
        prec: cfg.prec,
        units: cfg.units,
        back: sorted.slice(0, 5),
        middle: sorted.slice(5, 10),
        front: sorted.slice(10, 13),
        // Two dragons are indistinguishable, so they wash rather than resolve.
        tie: null,
    };
}
function canonSixPairs(hand13, cfg) {
    const byRank = new Map();
    for (const c of hand13) {
        const r = RV(c);
        const bucket = byRank.get(r);
        if (bucket)
            bucket.push(c);
        else
            byRank.set(r, [c]);
    }
    const pairCards = [];
    const pairRanks = [];
    let single = null;
    for (const [r, cs] of [...byRank.entries()].sort((a, b) => b[0] - a[0])) {
        let i = 0;
        for (; i + 1 < cs.length; i += 2) {
            pairCards.push(cs[i], cs[i + 1]);
            pairRanks.push(r);
        }
        // Exactly one rank has an odd count when this fires, so the leftover is the
        // one card that is not in a pair. It goes in front.
        if (i < cs.length)
            single = cs[i];
    }
    return {
        kind: 'sixPairs',
        prec: cfg.prec,
        units: cfg.units,
        back: pairCards.slice(0, 5),
        middle: pairCards.slice(5, 10),
        front: pairCards.slice(10, 12).concat(single ? [single] : []),
        tie: [...pairRanks].sort((a, b) => b - a), // highest pair down
    };
}
function mkNat(found) {
    return {
        kind: found.kind,
        prec: found.cfg.prec,
        units: found.cfg.units,
        back: found.back,
        middle: found.middle,
        front: found.front,
        tie: natTieVec(found.back, found.middle, found.front),
    };
}
/**
 * The naturals catalogue. Dragons and six pairs are pure multiset properties of
 * the thirteen cards; the three-X naturals are arrangement-dependent and are
 * found by enumerating splits.
 *
 * This reads the HAND, never the submitted rows — see commit() in index.ts.
 */
export function detectNatural(hand13, rules) {
    const nat = rules.naturals;
    const rc = rankCounts(hand13);
    const sc = suitCounts(hand13);
    const distinct = rc.size === 13; // one of every rank
    // Precedence 8/7 — a dragon outranks everything, and a one-suit dragon
    // outranks a mixed one.
    if (distinct) {
        if (Object.values(sc).some((n) => n === 13) && nat.cleanDragon.on) {
            return canonDragon(hand13, 'cleanDragon', nat.cleanDragon);
        }
        if (nat.dragon.on)
            return canonDragon(hand13, 'dragon', nat.dragon);
    }
    // Precedence 6/4/3 — three straight flushes / three flushes / three straights.
    // Held in a one-slot array rather than a `let`, because the enumeration below
    // is the only writer and control-flow narrowing would otherwise fight it.
    const found = [];
    const wantSF = nat.threeStraightFlush.on;
    const wantFl = nat.threeFlush.on;
    const wantSt = nat.threeStraight.on;
    if ((wantSF || wantFl || wantSt) && threeXPossible(rc, sc)) {
        // Every legal 5/5/3 split — 1,287 backs × 56 middle/front partitions.
        scan: for (const bIdx of combos(13, 5)) {
            const inBack = new Set(bIdx);
            const rest8 = [];
            for (let i = 0; i < 13; i++)
                if (!inBack.has(i))
                    rest8.push(i);
            const back = bIdx.map((i) => hand13[i]);
            const bF = sameSuit(back);
            const bS = isStraight5(back);
            if (!(wantSF && bF && bS) && !(wantFl && bF) && !(wantSt && bS))
                continue;
            for (const [mIdx, fIdx] of MID_FRONT_8) {
                const middle = mIdx.map((j) => hand13[rest8[j]]);
                const front = fIdx.map((j) => hand13[rest8[j]]);
                const mF = sameSuit(middle);
                const mS = isStraight5(middle);
                const fF = sameSuit(front);
                const fS = is3StraightRv(front.map(RV));
                const best = found[0];
                if (wantSF && bF && bS && mF && mS && fS && fF) {
                    found[0] = { kind: 'threeStraightFlush', prec: nat.threeStraightFlush.prec, cfg: nat.threeStraightFlush, back, middle, front };
                    break scan; // nothing outranks it
                }
                // A hand can be BOTH three-flush and three-straight capable. Never lock
                // on the first match: the higher-precedence threeFlush has to win even
                // when a threeStraight split was enumerated first, or natural-vs-natural
                // precedence flips with enumeration order.
                if (wantFl && bF && mF && fF && (!best || best.prec < nat.threeFlush.prec)) {
                    found[0] = { kind: 'threeFlush', prec: nat.threeFlush.prec, cfg: nat.threeFlush, back, middle, front };
                }
                else if (wantSt && bS && mS && fS && !best) {
                    found[0] = { kind: 'threeStraight', prec: nat.threeStraight.prec, cfg: nat.threeStraight, back, middle, front };
                }
            }
        }
    }
    const three = found[0];
    if (three)
        return mkNat(three);
    // Precedence 2 — six pairs, i.e. exactly one rank left over after pairing up.
    if (nat.sixPairs.on) {
        let pairs = 0;
        let odd = 0;
        for (const n of rc.values()) {
            pairs += Math.floor(n / 2);
            if (n % 2 === 1)
                odd++;
        }
        if (pairs === 6 && odd === 1)
            return canonSixPairs(hand13, nat.sixPairs);
    }
    return null;
}
/** What one row earns per opponent. Front can only ever be trips. */
export function royaltyUnits(row, key, rules) {
    const R = rules.royalties[row];
    const cat = catOf(key);
    if (row === 'front')
        return cat === 3 ? R.trips ?? 0 : 0;
    switch (cat) {
        case 8:
            return R.straightFlush ?? 0;
        case 7:
            return R.quads ?? 0;
        case 6:
            return R.fullHouse ?? 0;
        case 5:
            return R.flush ?? 0;
        case 4:
            return R.straight ?? 0;
        default:
            return 0;
    }
}
/** Total royalty units a board earns per opponent. Missed boards collect none. */
export function boardRoyalties(board, rules) {
    return (royaltyUnits('back', board.keys.back, rules) +
        royaltyUnits('middle', board.keys.middle, rules) +
        royaltyUnits('front', board.keys.front, rules));
}
/** Negation that never produces -0: it survives a round trip through JSON as 0,
 * so a golden vector would compare unequal against the state that produced it. */
function neg(n) {
    return n === 0 ? 0 : -n;
}
function emptyBreakdown() {
    return { rows: null, unitsA: 0, unitsB: 0, scoop: null, royaltyA: 0, royaltyB: 0, natural: null };
}
function natTie(a, b) {
    const ta = a?.tie;
    const tb = b?.tie;
    if (!ta || !tb)
        return 0; // dragons — indistinguishable, so a genuine tie
    for (let i = 0; i < Math.min(ta.length, tb.length); i++) {
        const x = ta[i] ?? 0;
        const y = tb[i] ?? 0;
        if (x !== y)
            return x - y;
    }
    return 0;
}
function rowRoyalties(self, mode, side, rules, brk) {
    let total = 0;
    for (const row of ['back', 'middle', 'front']) {
        const u = royaltyUnits(row, self.keys[row], rules);
        if (u === 0)
            continue;
        if (mode === 'onlyIfRowWon' && brk.rows?.[row] !== side)
            continue;
        total += u;
    }
    return total;
}
/**
 * A's net against B, with the breakdown the results screen renders.
 *
 * Resolution order matters and is not negotiable: naturals → missets → rows →
 * scoop → royalties. A natural skips the row comparison entirely, and a misset
 * is settled before anybody's rows are read, which is why a missed board with a
 * quad in back still pays nothing for it.
 *
 * One divergence, and it moves no units: the reference leaves `unitsA`/`unitsB`
 * at zero when a natural resolves the pair, because its own client only reads
 * them off a row result. They are filled in here so the results screen can show
 * a per-pair number for every pair at the table, natural or not.
 */
export function pairNet(A, B, rules) {
    const scoopBonus = rules.scoopBonus;
    const brk = emptyBreakdown();
    // — naturals —
    if (A.natural || B.natural) {
        if (A.natural && B.natural) {
            if (A.natural.prec !== B.natural.prec) {
                const aWins = A.natural.prec > B.natural.prec;
                const units = aWins ? A.natural.units : B.natural.units;
                brk.natural = { win: aWins ? 'a' : 'b', units };
                brk.unitsA = aWins ? units : neg(units);
                brk.unitsB = neg(brk.unitsA);
                return { net: brk.unitsA, brk };
            }
            const cmp = natTie(A.natural, B.natural);
            if (cmp === 0) {
                brk.natural = { win: 'tie', units: 0 };
                return { net: 0, brk };
            }
            const aWins = cmp > 0;
            const units = aWins ? A.natural.units : B.natural.units;
            brk.natural = { win: aWins ? 'a' : 'b', units };
            brk.unitsA = aWins ? units : neg(units);
            brk.unitsB = neg(brk.unitsA);
            return { net: brk.unitsA, brk };
        }
        const aNat = A.natural !== null;
        const units = aNat ? (A.natural?.units ?? 0) : (B.natural?.units ?? 0);
        brk.natural = { win: aNat ? 'a' : 'b', units };
        brk.unitsA = aNat ? units : neg(units);
        brk.unitsB = neg(brk.unitsA);
        return { net: brk.unitsA, brk };
    }
    // — missets, with no natural in play —
    if (A.misset && B.misset)
        return { net: 0, brk }; // both missed → wash
    if (A.misset || B.misset) {
        const clean = A.misset ? 'b' : 'a';
        const cleanBoard = A.misset ? B : A;
        // The clean side collects its royalties; the misser pays and never collects.
        const roy = boardRoyalties(cleanBoard, rules);
        const swing = 3 + scoopBonus + roy; // all three rows, plus the scoop
        brk.rows = { back: clean, middle: clean, front: clean };
        brk.scoop = { winner: clean, bonus: scoopBonus };
        if (clean === 'a') {
            brk.royaltyA = roy;
            brk.unitsA = swing;
            brk.unitsB = neg(swing);
            return { net: swing, brk };
        }
        brk.royaltyB = roy;
        brk.unitsA = neg(swing);
        brk.unitsB = swing;
        return { net: -swing, brk };
    }
    // — both clean: row by row —
    const rBack = compareHands(A.back, B.back);
    const rMid = compareHands(A.middle, B.middle);
    const rFront = compareHands(A.front, B.front);
    let net = rBack + rMid + rFront;
    brk.rows = {
        back: rBack > 0 ? 'a' : rBack < 0 ? 'b' : 'tie',
        middle: rMid > 0 ? 'a' : rMid < 0 ? 'b' : 'tie',
        front: rFront > 0 ? 'a' : rFront < 0 ? 'b' : 'tie',
    };
    // A scoop needs all three rows won OUTRIGHT — a tied row is not a win.
    if (rBack > 0 && rMid > 0 && rFront > 0) {
        net += scoopBonus;
        brk.scoop = { winner: 'a', bonus: scoopBonus };
    }
    else if (rBack < 0 && rMid < 0 && rFront < 0) {
        net -= scoopBonus;
        brk.scoop = { winner: 'b', bonus: scoopBonus };
    }
    const royA = rowRoyalties(A, rules.royaltyMode, 'a', rules, brk);
    const royB = rowRoyalties(B, rules.royaltyMode, 'b', rules, brk);
    brk.royaltyA = royA;
    brk.royaltyB = royB;
    net += royA - royB;
    brk.unitsA = net;
    brk.unitsB = neg(net);
    return { net, brk };
}
/** Score every pair at the table. Seats are indices into `boards`. */
export function scoreTable(boards, rules) {
    const units = boards.map(() => 0);
    const pairwise = [];
    for (let a = 0; a < boards.length; a++) {
        for (let b = a + 1; b < boards.length; b++) {
            const A = boards[a];
            const B = boards[b];
            if (!A || !B)
                continue;
            const { net, brk } = pairNet(A, B, rules);
            units[a] = (units[a] ?? 0) + net;
            units[b] = (units[b] ?? 0) - net;
            pairwise.push({ a, b, ...brk });
        }
    }
    return { units, pairwise };
}
// ── §5 the auto-arranger ────────────────────────────────────────────────────
/**
 * Static win-probability estimates per row and hand class against a random
 * field. Monotone in category; the middle rewards full-house-and-up because
 * that is where a middle row actually decides pairs.
 */
const PCT = {
    back: [0.15, 0.3, 0.48, 0.62, 0.75, 0.85, 0.93, 0.98, 0.995],
    mid: [0.12, 0.26, 0.44, 0.6, 0.74, 0.86, 0.95, 0.99, 0.998],
    front: [0.2, 0.55, 0.0, 0.9], // front only ever reaches cats 0, 1 and 3
};
function pctOf(pos, cat) {
    const t = PCT[pos];
    return t[cat] ?? t[t.length - 1] ?? 0;
}
/** Dragons and six pairs, in O(13) — no enumeration needed to see them. */
function cheapNatural(hand13, rules) {
    const nat = rules.naturals;
    const rc = rankCounts(hand13);
    if (rc.size === 13) {
        const sc = suitCounts(hand13);
        if (Object.values(sc).some((n) => n === 13) && nat.cleanDragon.on) {
            return canonDragon(hand13, 'cleanDragon', nat.cleanDragon);
        }
        if (nat.dragon.on)
            return canonDragon(hand13, 'dragon', nat.dragon);
    }
    if (nat.sixPairs.on) {
        let pairs = 0;
        let odd = 0;
        for (const n of rc.values()) {
            pairs += Math.floor(n / 2);
            if (n % 2 === 1)
                odd++;
        }
        if (pairs === 6 && odd === 1)
            return canonSixPairs(hand13, nat.sixPairs);
    }
    return null;
}
/**
 * The best legal split of a hand, by full enumeration of all 72,072 of them.
 *
 * NEVER returns a misset — a legal split always exists, and this is what the
 * clock hands an absent player, so it has to be a board they would not be
 * ashamed of rather than merely a valid one. Cheap naturals short-circuit; the
 * arrangement-dependent ones are picked up inside the same pass.
 */
export function bestArrangement(hand13, rules) {
    if (hand13.length !== 13)
        throw new Error(`pusoy: bestArrangement needs 13 cards, got ${hand13.length}`);
    const cheap = cheapNatural(hand13, rules);
    if (cheap)
        return { back: cheap.back, middle: cheap.middle, front: cheap.front, natural: cheap };
    const nat = rules.naturals;
    const wantSF = nat.threeStraightFlush.on;
    const wantFl = nat.threeFlush.on;
    const wantSt = nat.threeStraight.on;
    const wantThree = wantSF || wantFl || wantSt;
    const rv = hand13.map(RV);
    const su = hand13.map(suitOf);
    const at = (i) => hand13[i];
    const r = (i) => rv[i];
    const s = (i) => su[i];
    let best = null;
    let bestNat = null;
    outer: for (const bIdx of combos(13, 5)) {
        const inBack = new Set(bIdx);
        const rest8 = [];
        for (let i = 0; i < 13; i++)
            if (!inBack.has(i))
                rest8.push(i);
        const b = bIdx;
        const b0 = b[0];
        const b1 = b[1];
        const b2 = b[2];
        const b3 = b[3];
        const b4 = b[4];
        const bk = evalFive([r(b0), r(b1), r(b2), r(b3), r(b4)], [s(b0), s(b1), s(b2), s(b3), s(b4)]);
        for (const [mIdx, fIdx] of MID_FRONT_8) {
            const m0 = rest8[mIdx[0]];
            const m1 = rest8[mIdx[1]];
            const m2 = rest8[mIdx[2]];
            const m3 = rest8[mIdx[3]];
            const m4 = rest8[mIdx[4]];
            const mk = evalFive([r(m0), r(m1), r(m2), r(m3), r(m4)], [s(m0), s(m1), s(m2), s(m3), s(m4)]);
            if (bk.key < mk.key)
                continue; // back < middle → misset
            const f0 = rest8[fIdx[0]];
            const f1 = rest8[fIdx[1]];
            const f2 = rest8[fIdx[2]];
            const fRv = [r(f0), r(f1), r(f2)];
            const fKey = evalThree(fRv);
            if (!middleGeFrontKeys(mk.key, fKey))
                continue; // middle < front → misset
            const back = [at(b0), at(b1), at(b2), at(b3), at(b4)];
            const middle = [at(m0), at(m1), at(m2), at(m3), at(m4)];
            const front = [at(f0), at(f1), at(f2)];
            if (wantThree) {
                const fFlush = s(f0) === s(f1) && s(f1) === s(f2);
                const fStr = is3StraightRv(fRv);
                if (wantSF && bk.straight && bk.flush && mk.straight && mk.flush && fStr && fFlush) {
                    bestNat = { kind: 'threeStraightFlush', prec: nat.threeStraightFlush.prec, cfg: nat.threeStraightFlush, back, middle, front };
                    break outer; // nothing outranks it
                }
                if (wantFl && bk.flush && mk.flush && fFlush && (!bestNat || bestNat.prec < nat.threeFlush.prec)) {
                    bestNat = { kind: 'threeFlush', prec: nat.threeFlush.prec, cfg: nat.threeFlush, back, middle, front };
                }
                else if (wantSt && bk.straight && mk.straight && fStr && (!bestNat || bestNat.prec < nat.threeStraight.prec)) {
                    bestNat = { kind: 'threeStraight', prec: nat.threeStraight.prec, cfg: nat.threeStraight, back, middle, front };
                }
            }
            const roy = royaltyUnits('back', bk.key, rules) +
                royaltyUnits('middle', mk.key, rules) +
                royaltyUnits('front', fKey, rules);
            const ev = rules.wRows * (pctOf('back', catOf(bk.key)) + pctOf('mid', catOf(mk.key)) + pctOf('front', catOf(fKey))) +
                rules.wRoyalty * roy;
            // Tie-break on raw strength, back first. The multipliers push this past
            // 2^53 so it is approximate — it is ported verbatim anyway, because what
            // it has to be is IDENTICAL everywhere, not precise.
            const tieKey = bk.key * 1e14 + mk.key * 1e7 + fKey;
            if (best === null || ev > best.ev + 1e-12 || (Math.abs(ev - best.ev) <= 1e-12 && tieKey > best.tieKey)) {
                best = { back, middle, front, ev, tieKey };
            }
        }
    }
    if (bestNat) {
        const natural = mkNat(bestNat);
        return { back: natural.back, middle: natural.middle, front: natural.front, natural };
    }
    // Unreachable: a non-missing split always exists for thirteen cards.
    if (!best)
        throw new Error('pusoy: no legal arrangement for this hand');
    return { back: best.back, middle: best.middle, front: best.front, natural: null };
}
//# sourceMappingURL=rules.js.map